import React, { useState, useEffect, useCallback, useRef } from 'react'; // Added useRef
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, wrap, useMotionValue, animate } from 'framer-motion'; // Added useMotionValue, animate
import { Button } from '../components/ui/button'; // Adjust path as needed
import { cn } from '../lib/utils'; // Adjust path as needed
import { useAuth } from '../contexts/AuthContext'; // Import useAuth


// Define the images for the gallery/modal
const galleryImages = ["/images/Input_1.png", "/images/Example_2.jpg"];

const LandingPage = () => {
  const { user } = useAuth(); // Get user status
  const [[imgIndex], setImgState] = useState([null]); // Simpler state, only index needed now
  const dragX = useMotionValue(0); // Motion value to track drag position
  const containerRef = useRef(null); // Ref to get container width
  const slideRefs = useRef([]); // Refs for each slide to get their widths
  slideRefs.current = galleryImages.map((_, i) => slideRefs.current[i] ?? React.createRef());
  const [imageLoadStatus, setImageLoadStatus] = useState({}); // Track load status by index

  // Derive selectedImg from imgIndex
  const selectedImg = imgIndex !== null ? galleryImages[imgIndex] : null;

  // --- Centering Calculation Logic ---
  const calculateAndAnimateCenter = useCallback((targetIndex) => {
    if (targetIndex === null) return; // Don't calculate if no image selected

    const clampedIndex = Math.max(0, Math.min(targetIndex, galleryImages.length - 1));

    // Ensure refs and dimensions are ready
    if (!containerRef.current || !slideRefs.current[clampedIndex]?.current) {
      console.warn(`calculateAndAnimateCenter: Refs not ready for index ${clampedIndex}.`);
      return;
    }
    const viewportWidth = containerRef.current.offsetWidth;
    const currentSlideRef = slideRefs.current[clampedIndex].current;
    const currentSlideWidth = currentSlideRef.offsetWidth;

    if (viewportWidth <= 0 || currentSlideWidth <= 0) {
      console.warn(`calculateAndAnimateCenter: Invalid dimensions for index ${clampedIndex}. Viewport: ${viewportWidth}, Slide: ${currentSlideWidth}.`);
      return;
    }

    let precedingSlidesWidth = 0;
    const gap = 75; // The gap we added
    let calculationPossible = true;

    // Calculate total width of preceding slides, checking each ref
    for (let i = 0; i < clampedIndex; i++) {
      const slideRef = slideRefs.current[i]?.current;
      if (slideRef && slideRef.offsetWidth > 0) {
        precedingSlidesWidth += slideRef.offsetWidth;
      } else {
        console.warn(`calculateAndAnimateCenter: Ref or width not ready for preceding slide ${i} during calculation for index ${clampedIndex}. Aborting calculation.`);
        calculationPossible = false;
        break; // Stop calculation if any preceding width is missing
      }
    }

    if (!calculationPossible) return; // Abort if preceding widths weren't ready

    // Calculate the target offset for centering
    const totalPrecedingWidthAndGaps = precedingSlidesWidth + (clampedIndex * gap);
    const targetSlideCenterOffset = totalPrecedingWidthAndGaps + (currentSlideWidth / 2);
    const viewportCenter = viewportWidth / 2;
    const targetDragX = viewportCenter - targetSlideCenterOffset;

    // Animate the motion value to the new position
    animate(dragX, targetDragX, {
      type: "tween",
      duration: 0.2,
      ease: "easeInOut",
    });
  }, [dragX]); // Dependency dragX

  // --- State Update Logic ---
  const setActiveIndex = useCallback((newIndex) => {
    const clampedIndex = Math.max(0, Math.min(newIndex, galleryImages.length - 1));
    setImgState([clampedIndex]);
    // Note: The actual animation is triggered by the useEffect watching imgIndex
  }, []); // No dependencies needed here

  const showNextImage = useCallback(() => {
    if (imgIndex !== null && imgIndex < galleryImages.length - 1) {
      setActiveIndex(imgIndex + 1);
    }
  }, [imgIndex, setActiveIndex]);

  const showPrevImage = useCallback(() => {
    if (imgIndex !== null && imgIndex > 0) {
      setActiveIndex(imgIndex - 1);
    }
  }, [imgIndex, setActiveIndex]);

  // Function to set the initial image index when clicking a thumbnail
  const openModalAtIndex = (index) => {
    setActiveIndex(index); // Just set the state
  };

  // --- Effects ---

  // Effect to run centering calculation when index changes, container resizes, OR image loads
  useEffect(() => {
    // Conditions to run calculation: index selected, container ref ready, current image loaded
    if (imgIndex === null || !containerRef.current || !imageLoadStatus[imgIndex]) {
      return;
    }

    // Define the calculation function to be called by observer/initial run
    const runCalculation = () => calculateAndAnimateCenter(imgIndex);

    // Initial calculation attempt now that conditions are met
    runCalculation();

    // Set up ResizeObserver to recalculate on container resize
    const observer = new ResizeObserver(runCalculation);
    observer.observe(containerRef.current);

    // Cleanup observer on unmount or when dependencies change
    return () => {
      observer.disconnect();
    };
    // Depend on imgIndex, calculation function, and load status object
  }, [imgIndex, calculateAndAnimateCenter, imageLoadStatus]);


  // Effect to prevent body scroll when modal is open
  useEffect(() => {
    if (selectedImg) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = ''; // Revert to default
    }
    // Cleanup function to ensure scroll is restored if component unmounts while modal is open
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedImg]); // Dependency array ensures this runs when selectedImg changes

  // Swipe gesture handling
  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const handleDragEnd = (e, { offset, velocity }) => {
    const swipe = swipePower(offset.x, velocity.x);
    const containerWidth = containerRef.current ? containerRef.current.offsetWidth : window.innerWidth; // Get width (This is now 90% of screen)

    // Determine target index based on swipe
    if (swipe < -swipeConfidenceThreshold && imgIndex < galleryImages.length - 1) {
      // Swipe left (request next image)
      setActiveIndex(imgIndex + 1);
    } else if (swipe > swipeConfidenceThreshold && imgIndex > 0) {
      // Swipe right (request previous image)
      setActiveIndex(imgIndex - 1);
    } else {
      // If swipe wasn't strong enough, determine the closest index based on current drag position
      // and animate back to that index's position.
      const currentPosition = dragX.get();
      // Calculate the index the user was closest to when they stopped dragging
      // NOTE: This calculation is still based on containerWidth and might be inaccurate
      // with variable slide widths + gaps. Fixing drag-end snapping requires
      // more complex logic similar to the centering calculation.
      const closestIndex = Math.max(0, Math.min(Math.round(-currentPosition / containerWidth), galleryImages.length - 1));
      setActiveIndex(closestIndex); // Animate back towards the closest index (using the improved centering logic)
    }
  };

  // Removed the useEffect that animated dragX based on imgIndex changes,
  // as setActiveIndex now handles this animation directly.

  // The Header is now handled globally in App.js
  return (
    // Re-adding a wrapper div with the original dark theme classes
    <div className="bg-gradient-to-br from-gray-900 to-black text-white min-h-screen flex flex-col"> {/* Ensure full height and flex column */}
      {/* Main Content */}
      {/* Note: The <main> tag is now in App.js */}
      {/* Hero Section */}
      <section className="pb-8 md:pt-1 md:pb-8 px-6 md:px-10 text-center bg-black/10"> {/* Matched bottom padding to heading margin */}
          <h1 className="text-4xl md:text-6xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400"> {/* Doubled bottom margin */}
            Generate Professional Headshots with AI
          </h1>
          <div className="flex justify-center items-center gap-4 md:gap-8 mb-8">
            {/* Before Image */}
            <motion.img
              src="/images/Input_1.png"
              alt="Before Example"
              className="w-32 h-32 md:w-48 md:h-48 object-cover rounded-lg shadow-lg border-2 border-white/20 cursor-pointer transition-transform duration-300 hover:scale-105"
              onClick={() => openModalAtIndex(0)} // Use index 0
              layoutId="image-input" // For potential shared layout animation
            />
            <span className="text-3xl font-bold text-purple-400">→</span>
            {/* After Image */}
            <motion.img
              src="/images/Example_2.jpg"
              alt="After Example"
              className="w-32 h-32 md:w-48 md:h-48 object-cover rounded-lg shadow-lg border-2 border-white/20 cursor-pointer transition-transform duration-300 hover:scale-105"
              onClick={() => openModalAtIndex(1)} // Use index 1
              layoutId="image-output" // For potential shared layout animation
            />
           </div>
           {/* Update Link destination based on user status */}
           <Link to={user ? '/app' : '/login'}>
             <Button
               size="lg"
              className={cn(
                "py-3 px-8 text-white font-semibold rounded-lg",
                "bg-gradient-to-r from-purple-500 to-blue-500",
                "hover:from-purple-600 hover:to-blue-600",
                "transition-all duration-300",
                "shadow-lg hover:shadow-xl"
              )}
            >
              Get Started
            </Button>
          </Link>
        </section>

        {/* How It Works Section */}
        <section className="pt-8 pb-16 md:pt-10 md:pb-20 px-6 md:px-10 bg-gray-900/50"> {/* Halved top padding */}
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
            Simple Steps to Your New Headshot
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"> {/* Reduced gap */}
            <div className="text-center p-4 bg-white/5 rounded-lg border border-white/10"> {/* Reduced padding */}
              <div className="text-4xl mb-4 text-purple-400">1</div>
              <h3 className="text-xl font-semibold mb-2 text-white">Upload Photo</h3>
              <p className="text-gray-300">Upload a clear photo of yourself.</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg border border-white/10"> {/* Reduced padding */}
              <div className="text-4xl mb-4 text-purple-400">2</div>
              <h3 className="text-xl font-semibold mb-2 text-white">AI Processing</h3>
              <p className="text-gray-300">Our AI analyzes and transforms your image.</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg border border-white/10"> {/* Reduced padding */}
              <div className="text-4xl mb-4 text-purple-400">3</div>
              <h3 className="text-xl font-semibold mb-2 text-white">Download Headshot</h3>
              <p className="text-gray-300">Download your high-quality headshots.</p>
            </div>
          </div>
        </section>
      {/* Removed orphaned </main> tag */}

      {/* Footer */}
      <footer className="py-6 px-6 md:px-10 text-center text-gray-400 border-t border-white/10 mt-auto">
        <p className="mb-2">&copy; 2025 Headshot AI. All rights reserved.</p>
        <div className="space-x-4">
          <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
          <span>|</span>
          <Link to="/terms" className="hover:text-white">Terms of Service</Link>
        </div>
      </footer>

      {/* Modal for Enlarged Image */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            initial={{ opacity: 0 }} // Add fade-in for the overlay itself
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-hidden" // Added overflow-hidden to main modal container
            onClick={() => setImgState([null])} // Close on overlay click
          >
            {/* Outer container - Keep overflow-hidden */}
            <div className="relative w-full h-full flex items-center justify-center overflow-hidden" onClick={(e) => e.stopPropagation()}>

              {/* Viewport Container - Full width, no padding */}
              <div ref={containerRef} className="relative w-full h-full flex items-center justify-center"> {/* Reverted to w-full, removed px-12 */}

                {/* Inner Draggable Container that slides */}
                <motion.div
                  className="flex h-full w-full gap-x-[75px]" // Added gap between slides
                  drag="x" // Make this inner container draggable
                  dragConstraints={{
                    // Limit dragging based on the number of images and the container width
                    left: -(galleryImages.length - 1) * (containerRef.current ? containerRef.current.offsetWidth : window.innerWidth),
                    right: 0,
                  }}
                  dragElastic={0.1} // Reduce elasticity
                  onDragEnd={handleDragEnd} // Handle swipe gesture on drag end
                  // Remove explicit width style, control position with motion value
                  style={{ x: dragX }}
                  // No transition needed here, handled by animate() calls
                >
                  {/* Map over images and render each one inside a narrower wrapper */}
                  {galleryImages.map((imgSrc, index) => (
                    // Wrapper div now shrinks to fit content, assign ref
                    <div
                      key={imgSrc}
                      ref={slideRefs.current[index]} // Assign ref to measure width
                      className="flex-shrink-0 h-full flex items-center justify-center"
                    >

                      {/* New Relative Wrapper for Image and Buttons - Shrinks to fit image */}
                      <div className="relative inline-block max-w-[85vw] max-h-[90vh]"> {/* Increased max-w back to 85vw */}

                        {/* Close Button (Positioned relative to image wrapper) */}
                        <button
                          onClick={() => setImgState([null])}
                          // Position at the top-right corner of the image
                          className="absolute top-0 right-0 z-[70] text-white bg-gray-800/70 rounded-full p-2 hover:bg-gray-700/90 transition-colors shadow-lg" // Changed top-4 right-4 to top-0 right-0
                          aria-label="Close image viewer"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>

                        {/* Previous Button (Positioned relative to image wrapper) - Conditionally render */}
                        {imgIndex > 0 && index === imgIndex && ( // Only show on current slide
                          <button
                            onClick={showPrevImage}
                            // Position at the left edge of the image
                            className="absolute left-0 top-1/2 -translate-y-1/2 z-[60] text-white bg-black/50 rounded-full p-3 hover:bg-black/80 transition-colors" // Changed left-4 to left-0
                            aria-label="Previous image"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                        )}

                        {/* Next Button (Positioned relative to image wrapper) - Conditionally render */}
                        {imgIndex < galleryImages.length - 1 && index === imgIndex && ( // Only show on current slide
                          <button
                            onClick={showNextImage}
                            // Position at the right edge of the image
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-[60] text-white bg-black/50 rounded-full p-3 hover:bg-black/80 transition-colors" // Changed right-4 to right-0
                            aria-label="Next image"
                          >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        )}

                        <motion.img
                          src={imgSrc}
                          alt={`Enlarged view ${index + 1}`}
                          // Image width auto, constrained by height and wrapper
                          className="block w-auto h-auto object-contain max-h-[inherit]" // Inherit max-h from parent, width auto
                          // Prevent dragging the image itself, drag the container
                          draggable="false"
                          onLoad={() => {
                            // Mark this specific image as loaded
                            setImageLoadStatus(prev => ({ ...prev, [index]: true }));
                          }}
                          // Reset load status if src changes (though unlikely here)
                          key={imgSrc} // Add key based on src to help React detect changes
                        />
                      </div> {/* Close New Relative Wrapper */}
                    </div>
                  ))}
                </motion.div> {/* Close Inner Draggable Container */}
              </div> {/* Close Viewport Container */}
            </div> {/* Close Outer container */}
          </motion.div> // Close modal overlay
        )}
      </AnimatePresence>
    </div> // Close the wrapper div
  );
};

export default LandingPage;
